import { Head, Link } from "@inertiajs/react";
import { ArrowRight } from "lucide-react";

import { ActivationGuide } from "@/components/activation-guide";
import PublicHeader from "@/components/public-header";
import { SiteFooter } from "@/components/site-footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface OgImageSizeFaq {
  question: string;
  answer: string;
}

interface OgImageSizeProps {
  page_title?: string;
  faqs?: OgImageSizeFaq[];
}

interface SizeRow {
  platform: string;
  recommended: string;
  ratio: string;
  minimum: string;
  maxFile: string;
  formats: string;
  crop: string;
  tags: string;
}

const SIZE_ROWS: SizeRow[] = [
  {
    platform: "Facebook",
    recommended: "At least 1200×630 for high-resolution devices.",
    ratio: "Close to 1.91:1.",
    minimum: "200×200. Use at least 600×315 for a larger link post. Smaller than that still shows, but much smaller.",
    maxFile: "Must not exceed 8 MB.",
    formats: "Not stated on the images page.",
    crop: "Stay near 1.91:1 so Feed can show the full image without cropping. No safe-area inset is published.",
    tags: "og:image. Set og:image:width and og:image:height so the crawler can render before it downloads the file.",
  },
  {
    platform: "X (Twitter)",
    recommended: "Not published.",
    ratio: "Not published.",
    minimum: "Not published.",
    maxFile: "Not published.",
    formats: "Not published.",
    crop: "Not published.",
    tags: "No current Cards markup reference on docs.x.com to cite. The old size page now redirects to the platform overview.",
  },
  {
    platform: "LinkedIn",
    recommended:
      "1200×627 is the sharing-module minimum. Recommended ratio is 1.91:1. No separate larger size is published.",
    ratio: "1.91:1.",
    minimum: "1200×627. The same help page says images under 401 px wide display as a thumbnail.",
    maxFile: "5 MB for the sharing module.",
    formats:
      "Not stated for the sharing module. JPG, PNG, and GIF are listed under single-image ads, a different spec.",
    crop: "No safe area for link shares. A tip on the same page says square and vertical images might be cropped when ads are shared organically.",
    tags: "og:title, og:image, og:description, og:url. Width and height are not in that list.",
  },
  {
    platform: "Slack",
    recommended: "Not published.",
    ratio: "Not published.",
    minimum: "Not published.",
    maxFile: "Not published.",
    formats: "Not published.",
    crop: "Not published.",
    tags: "Slackbot-LinkExpanding looks for oEmbed, Twitter Card tags, and Open Graph tags, then fetches an image the tags point at.",
  },
  {
    platform: "WhatsApp",
    recommended: "No single pixel size. Under 600 KB, at least 300 px wide, width/height of 4:1 or less.",
    ratio: "4:1 or less (width ÷ height).",
    minimum: "300 px wide.",
    maxFile: "Under 600 KB.",
    formats: "Not stated.",
    crop: "Not published. WhatsApp may relax the rules, and says not to rely on that.",
    tags: "og:title, og:description, og:url, and og:image in the head. The image URL must be absolute. The head must sit inside the first 300 KB of HTML.",
  },
  {
    platform: "iMessage",
    recommended: "At least 900 px wide. No height is published. TN3156 calls this a guideline.",
    ratio: "Not published.",
    minimum: "Under 150 px wide may be ignored or shown as an icon. Icons should be square and at least 108 px.",
    maxFile:
      "No per-image cap. The page itself is limited to 1 MB. Icons, images, and videos together are limited to 10 MB.",
    formats: "Not stated for the preview image.",
    crop: "The preview size changes by device. Avoid text in the image. No safe-area inset is published.",
    tags: "og:image and og:title. Put the site name in og:site_name, not in the title. Also supply a favicon, apple-touch-icon, or link rel icon.",
  },
  {
    platform: "Discord",
    recommended:
      "Not published. The quick-start sample sets width 1200 and height 630. That sample is not a requirement.",
    ratio: "Not published.",
    minimum: "Not published.",
    maxFile: "Not published. A link shows at most 4 images.",
    formats: ".png, .gif, .jpg, .jpeg, .webp, and .avif.",
    crop: "Not published. summary_large_image shows a large image under the text. With no layout tag, the image is small. Two or more og:image tags become a gallery.",
    tags: "og:title or twitter:title, og:description or twitter:description, og:image or twitter:image, plus width, height, and og:image:type. twitter:card summary_large_image selects the large layout.",
  },
];

const META_SAMPLE = `<meta property="og:title" content="Page title" />
<meta property="og:description" content="One or two sentences. WhatsApp says about 80 characters is enough." />
<meta property="og:url" content="https://example.com/page" />
<meta property="og:image" content="https://cdn.example.com/og.jpg" />
<meta property="og:image:secure_url" content="https://cdn.example.com/og.jpg" />
<meta property="og:image:type" content="image/jpeg" />
<meta property="og:image:width" content="1200" />
<meta property="og:image:height" content="630" />
<meta property="og:image:alt" content="What the image shows" />
<meta name="twitter:card" content="summary_large_image" />
<meta name="twitter:image" content="https://cdn.example.com/og.jpg" />
<meta name="twitter:image:alt" content="What the image shows" />`;

const API_SAMPLE = `curl -X POST "https://ogpilot.com/api/v1/images" \\
  -H "Content-Type: application/json" \\
  -H "Accept: application/json" \\
  -d '{"token":"YOUR_JWT_TOKEN"}'

# Documented completed JSON includes:
# "width": 1200,
# "height": 630,
# "url": "https://cdn.ogpilot.com/..."

# There is no width or height request field.
# Required JWT claims: iss (verified domain), sub (API key prefix).
# Optional: iat, template, title, description, path.
# Sign the JWT on your server. Docs: https://ogpilot.com/docs`;

const MCP_SAMPLE = `Remote MCP URL: https://ogpilot.com/mcp
Transport: Streamable HTTP
Auth: OAuth 2.1 with PKCE
Docs: https://ogpilot.com/docs/mcp

generate_og_image
{ "url": "https://example.com/pricing" }
Optional style. Default is auto-detect.
Returns image_url. The schema has no width or height field.
The card is rendered on the 1200×630 template.

compress_image
{
  "image_url": "https://cdn.example.com/og.jpg",
  "width": 1200,
  "height": 630
}
width defaults to 1200. height defaults to 630.
format defaults to jpeg. fit defaults to cover.

generate_og_image needs a signed-in account with an active plan.
compress_image works on a guest connector session.`;

const sectionClass =
  "mt-16 rounded-3xl border border-orange-200/80 bg-white/90 p-6 shadow-xl sm:p-8 dark:border-orange-500/25 dark:bg-slate-900/80 dark:shadow-none";

const linkClass = "font-medium text-orange-700 hover:underline dark:text-orange-300";

const COLUMNS: { key: keyof Omit<SizeRow, "platform">; label: string }[] = [
  { key: "recommended", label: "Recommended" },
  { key: "ratio", label: "Aspect ratio" },
  { key: "minimum", label: "Minimum" },
  { key: "maxFile", label: "Max file size" },
  { key: "formats", label: "Formats" },
  { key: "crop", label: "Crop / safe area" },
  { key: "tags", label: "Meta tags" },
];

function CodeSample({ label, code }: { label: string; code: string }) {
  return (
    <div className="mt-6">
      <p className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100">{label}</p>
      <pre className="overflow-x-auto rounded-2xl border border-orange-200 bg-orange-50 p-5 text-sm leading-relaxed text-slate-900 dark:border-orange-500/30 dark:bg-slate-950 dark:text-orange-50">
        <code>{code}</code>
      </pre>
    </div>
  );
}

export default function OgImageSize({
  page_title: pageTitle = "og:image Size Cheat Sheet 2026",
  faqs = [],
}: OgImageSizeProps) {
  return (
    <>
      <Head title={pageTitle} />

      <div className="min-h-screen bg-[radial-gradient(circle_at_0%_10%,#fed7aa_0,transparent_45%),radial-gradient(circle_at_100%_0%,#fecaca_0,transparent_38%),linear-gradient(180deg,#fff7ed_0%,#ffffff_32%,#fffbeb_100%)] pb-20 dark:bg-[radial-gradient(circle_at_0%_10%,#431407_0,transparent_45%),radial-gradient(circle_at_100%_0%,#7f1d1d_0,transparent_38%),linear-gradient(180deg,#0f172a_0%,#111827_32%,#0b1120_100%)]">
        <div className="container mx-auto max-w-6xl px-4 py-12">
          <PublicHeader />

          <section className="relative mt-8 overflow-hidden rounded-[2.25rem] border border-orange-200/80 bg-white/85 p-8 shadow-[0_35px_70px_-40px_rgba(194,65,12,0.55)] backdrop-blur sm:p-10 dark:border-orange-500/30 dark:bg-slate-900/80 dark:shadow-[0_35px_70px_-40px_rgba(249,115,22,0.35)]">
            <div className="relative">
              <Badge className="mb-5 border border-orange-200 bg-orange-100 px-4 py-1.5 tracking-[0.22em] text-orange-700 uppercase dark:border-orange-500/40 dark:bg-orange-500/20 dark:text-orange-100">
                og:image size
              </Badge>
              <h1 className="max-w-4xl text-4xl leading-tight font-bold text-balance text-slate-900 sm:text-5xl dark:text-slate-100">
                og:image size cheat sheet 2026
              </h1>
              <p className="mt-6 max-w-3xl text-base leading-relaxed text-slate-700 sm:text-lg dark:text-slate-300">
                Ship <strong>1200×630</strong>, close to <strong>1.91:1</strong>, and keep the file{" "}
                <strong>under 600 KB</strong>. That is the open graph image size that meets the floors and caps
                Facebook, LinkedIn, WhatsApp, and iMessage actually publish. X does not publish a current size.
              </p>
            </div>
          </section>

          <section id="size-table" className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
              Platform sizes for og image size queries
            </h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              Every number below is from the vendor page linked under the table. A cell says &quot;Not published&quot;
              when that page does not state the figure. Telegram is omitted: its public API describes link previews, and
              it does not publish an og:image pixel size, file cap, or format list.
            </p>
            <div className="mt-6 overflow-x-auto">
              <table className="w-full min-w-[72rem] border-collapse text-left text-sm text-slate-700 dark:text-slate-300">
                <thead>
                  <tr className="border-b border-orange-200 dark:border-orange-500/30">
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">Platform</th>
                    {COLUMNS.map((column) => (
                      <th key={column.key} className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {SIZE_ROWS.map((row) => (
                    <tr key={row.platform} className="border-b border-orange-100 align-top dark:border-orange-500/20">
                      <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">{row.platform}</th>
                      {COLUMNS.map((column) => (
                        <td key={column.key} className="px-3 py-3">
                          {row[column.key]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Sources:{" "}
              <a
                href="https://developers.facebook.com/docs/sharing/webmasters/images/"
                className={linkClass}
                target="_blank"
                rel="noopener noreferrer"
              >
                Meta, Images in Link Shares
              </a>
              ,{" "}
              <a href="https://ogp.me/" className={linkClass} target="_blank" rel="noopener noreferrer">
                Open Graph protocol
              </a>
              ,{" "}
              <a href="https://docs.x.com/overview" className={linkClass} target="_blank" rel="noopener noreferrer">
                X developer overview
              </a>{" "}
              (the old large-image card URL redirects here),{" "}
              <a
                href="https://www.linkedin.com/help/linkedin/answer/a521928"
                className={linkClass}
                target="_blank"
                rel="noopener noreferrer"
              >
                LinkedIn, make your website shareable
              </a>
              ,{" "}
              <a href="https://api.slack.com/robots" className={linkClass} target="_blank" rel="noopener noreferrer">
                Slack robots
              </a>
              ,{" "}
              <a
                href="https://docs.slack.dev/messaging/unfurling-links-in-messages/"
                className={linkClass}
                target="_blank"
                rel="noopener noreferrer"
              >
                Slack unfurls
              </a>
              ,{" "}
              <a
                href="https://developers.facebook.com/docs/whatsapp/link-previews/"
                className={linkClass}
                target="_blank"
                rel="noopener noreferrer"
              >
                WhatsApp link previews
              </a>
              ,{" "}
              <a
                href="https://developer.apple.com/documentation/technotes/tn3156-create-rich-previews-for-messages"
                className={linkClass}
                target="_blank"
                rel="noopener noreferrer"
              >
                Apple TN3156
              </a>
              , and{" "}
              <a
                href="https://docs.discord.com/developers/link-previews/overview"
                className={linkClass}
                target="_blank"
                rel="noopener noreferrer"
              >
                Discord link previews
              </a>
              .
            </p>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Copy-paste tags for 1200×630</h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              <code>og:image</code>, <code>og:image:width</code>, and <code>og:image:height</code> are in Meta&apos;s
              images doc and on ogp.me. <code>og:image:type</code>, <code>og:image:secure_url</code>, and{" "}
              <code>og:image:alt</code> are protocol properties. <code>twitter:card</code>{" "}
              <code>summary_large_image</code> is the value Discord uses for a large image, and the value Apple names
              for social-post previews. WhatsApp&apos;s doc only requires <code>og:title</code>,{" "}
              <code>og:description</code>, <code>og:url</code>, and an absolute <code>og:image</code>.
            </p>
            <CodeSample label="HTML meta tags" code={META_SAMPLE} />
            <CodeSample label="OG Pilot API request (1200×630 response)" code={API_SAMPLE} />
            <p className="mt-4 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              <code>POST /api/v1/images</code> takes <code>token</code> only. The documented JSON response reports{" "}
              <code>width</code> 1200 and <code>height</code> 630. The template canvas is 1200×630, and the preview
              variant is a JPEG limited to that size. Prefer an SDK over hand-rolled signing. Setup is on{" "}
              <Link href="/docs" className={linkClass}>
                /docs
              </Link>
              .
            </p>
            <CodeSample label="MCP config and tool calls" code={MCP_SAMPLE} />
            <p className="mt-4 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              <code>generate_og_image</code> builds the card. <code>compress_image</code> is the call that takes{" "}
              <code>width</code> and <code>height</code>. Connector steps are on{" "}
              <Link href="/docs/mcp" className={linkClass}>
                /docs/mcp
              </Link>
              .
            </p>
          </section>

          <section className="mt-16 rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-orange-900 p-8 text-white shadow-xl sm:p-10">
            <h2 className="text-3xl font-bold">Get an API key, or install MCP</h2>
            <p className="mt-4 max-w-2xl text-sm leading-6 text-slate-200">
              The table above is the size. The API returns the 1200×630 file. MCP lets an agent generate that file or
              resize one you already host.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Button asChild size="lg" className="bg-white text-slate-900 hover:bg-slate-100">
                <Link href="/sign_up">
                  Get an API key
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white/40 bg-transparent text-white hover:bg-white/10"
              >
                <Link href="/docs/mcp">Install MCP</Link>
              </Button>
            </div>
            <p className="mt-4 text-sm text-slate-200">
              Or{" "}
              <Link href="/new" className="font-semibold text-white underline">
                generate one now
              </Link>{" "}
              before you wire auth.
            </p>
          </section>

          <ActivationGuide
            title="Check the card, then pick a stack"
            links={[
              {
                href: "/open-graph-debugger",
                label: "Open Graph debugger",
                detail: "See the tags Facebook, X, LinkedIn, Slack, Discord, and WhatsApp are likely to read.",
              },
              {
                href: "/og-image-api",
                label: "OG image API",
                detail: "Hosted API next to @vercel/og and a DIY Satori pipeline.",
              },
              {
                href: "/nextjs-og-image-generator",
                label: "Next.js OG images",
                detail: "Metadata file, ImageResponse, or this API from generateMetadata.",
              },
              {
                href: "/wordpress",
                label: "WordPress plugin",
                detail: "Generated og:image for posts, pages, and WooCommerce.",
              },
              {
                href: "/change-og-image-wordpress",
                label: "Change the WordPress OG image",
                detail: "Featured image, SEO-plugin social image, or a generated card.",
              },
              {
                href: "/docs/mcp",
                label: "MCP connector",
                detail: "Remote tools for generate_og_image and compress_image.",
              },
              {
                href: "/new",
                label: "Generate one image",
                detail: "A single 1200×630 card, before you sign requests.",
              },
            ]}
          >
            <p>
              After the tags are live, run the URL through the{" "}
              <Link href="/open-graph-debugger" className={linkClass}>
                Open Graph debugger
              </Link>
              . Framework paths for the same image are the{" "}
              <Link href="/og-image-api" className={linkClass}>
                OG image API comparison
              </Link>
              , the{" "}
              <Link href="/nextjs-og-image-generator" className={linkClass}>
                Next.js guide
              </Link>
              , and the{" "}
              <Link href="/wordpress" className={linkClass}>
                WordPress plugin
              </Link>
              , and{" "}
              <Link href="/astro-og-image" className={linkClass}>
                dynamic OG images for Astro
              </Link>
              .
            </p>
          </ActivationGuide>

          {faqs.length > 0 ? (
            <section className={sectionClass}>
              <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">og:image size questions</h2>
              <div className="mt-6 space-y-6">
                {faqs.map((faq) => (
                  <article key={faq.question}>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{faq.question}</h3>
                    <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">{faq.answer}</p>
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          <SiteFooter className="mt-16" showSeparator={false} />
        </div>
      </div>
    </>
  );
}
